import { LDFlagSet } from 'launchdarkly-react-client-sdk';
import { memoize } from 'micro-memoize';

import { ApiView, ApiViewState, getDefaultApiViewState } from '../../../core/ApiView';
import { consolidationsQuery } from '../../../queries/consolidations';
import { currentUserQuery } from '../../../queries/currentUser';
import { deleteConsolidationMutation, updateWorkspaceMutation } from '../../../mutations/workspaces';
import { deleteOrganisationMutation } from '../../../operations/workspaces/mutations/deleteOrganisation/deleteOrganisationMutation';
import { ExtendedWorkspaceListItem, processWorkspace } from '../../../operations/workspaces/processors/workspacesList';
import { integrationsQuery } from '../../../queries/integrations';
import { reconnectOrganisationMutation } from '../../../operations/workspaces/mutations/reconnectOrganisation/reconnectOrganisationMutation';
import { removeOrganisationAccessMutation } from '../../../operations/workspaces/mutations/removeOrganisationAccess/removeOrganisationAccessMutation';
import { workspacesListingQuery } from '../../../operations/workspaces/queries/workspacesListingQuery';

export interface ModifyWorkspaceViewState extends ApiViewState {
  errorReconnecting: Error | null;
  errorUpdating: Error | null;
  isDefaultWorkspace?: boolean;
  isDeletingConsolidation: boolean;
  isDeletingOrganisation: boolean;
  isReconnecting: boolean;
  isRemovingAccess: boolean;
  isUpdating: boolean;
  workspace?: ExtendedWorkspaceListItem;
}

export class ModifyWorkspaceView extends ApiView {
  protected consolidationsObs: ReturnType<typeof consolidationsQuery>;
  protected currentUserObs: ReturnType<typeof currentUserQuery>;
  protected integrationsObs: ReturnType<typeof integrationsQuery>;
  protected workspacesObs: ReturnType<typeof workspacesListingQuery>;
  protected memoizedProcessWorkspace: typeof processWorkspace;
  private deleteConsolidationMutationObs: ReturnType<typeof deleteConsolidationMutation>;
  private deleteOrganisationMutationObs: ReturnType<typeof deleteOrganisationMutation>;
  private reconnectOrganisationMutationObs: ReturnType<typeof reconnectOrganisationMutation>;
  private removeOrganisationAccessMutationObs: ReturnType<typeof removeOrganisationAccessMutation>;
  private updateMutation: ReturnType<typeof updateWorkspaceMutation>;

  public state: ModifyWorkspaceViewState = {
    ...getDefaultApiViewState(),
    errorReconnecting: null,
    errorUpdating: null,
    isDefaultWorkspace: undefined,
    isDeletingConsolidation: false,
    isDeletingOrganisation: false,
    isReconnecting: false,
    isRemovingAccess: false,
    isUpdating: false
  };

  constructor(protected flags: LDFlagSet, protected workspaceId: string) {
    super();

    this.consolidationsObs = this.addQuery(consolidationsQuery(workspaceId));
    this.currentUserObs = this.addQuery(currentUserQuery());
    this.deleteConsolidationMutationObs = this.addMutation(deleteConsolidationMutation());
    this.deleteOrganisationMutationObs = this.addMutation(deleteOrganisationMutation());
    this.integrationsObs = this.addQuery(integrationsQuery());
    this.reconnectOrganisationMutationObs = this.addMutation(reconnectOrganisationMutation());
    this.removeOrganisationAccessMutationObs = this.addMutation(removeOrganisationAccessMutation());
    this.workspacesObs = this.addQuery(workspacesListingQuery());
    this.updateMutation = this.addMutation(updateWorkspaceMutation());
    this.memoizedProcessWorkspace = memoize(processWorkspace);

    this.updateState();
  }

  // ***************
  // *  Public API *
  // ***************

  public deleteConsolidation(consolidationId: string) {
    return this.deleteConsolidationMutationObs.mutate({ consolidationId, workspaceId: this.workspaceId });
  }

  public deleteOrganisation(organisationId: string) {
    return this.deleteOrganisationMutationObs.mutate({ workspaceId: this.workspaceId, organisationId });
  }

  public reconnectOrganisation(organisationId: string, successUrl: string) {
    const organisation = this.rawWorkspace?.organisations?.find(item => item.id === organisationId);
    const datasource = organisation?.datasource;
    const integration = this.integrations?.find(item => item.id === datasource?.integration_id);
    const currentUser = this.currentUser;

    if (!datasource || !integration || !currentUser) {
      return Promise.reject(new Error('Unable to reconnect organisation: connection data is unavailable'));
    }

    return this.reconnectOrganisationMutationObs.mutate({
      datasourceId: datasource.id,
      integrationSlug: integration.slug,
      organisationId,
      successUrl,
      userId: currentUser.id,
      workspaceId: this.workspaceId
    });
  }

  public removeAccess(organisationId: string) {
    return this.removeOrganisationAccessMutationObs.mutate({ workspaceId: this.workspaceId, organisationId });
  }

  public updateWorkspace(data: Partial<{ name: string | null; is_default_workspace: boolean }>) {
    return this.updateMutation.mutate({ workspaceId: this.workspaceId, data });
  }

  // ****************************
  // *  Private / Protected API *
  // ****************************

  private get errorReconnecting() {
    return this.reconnectOrganisationMutationObs.getCurrentResult().error;
  }

  private get errorUpdating() {
    return (
      this.deleteConsolidationMutationObs.getCurrentResult().error ||
      this.deleteOrganisationMutationObs.getCurrentResult().error ||
      this.removeOrganisationAccessMutationObs.getCurrentResult().error ||
      this.updateMutation.getCurrentResult().error
    );
  }

  private get isDefaultWorkspace() {
    return this.workspace?.member?.is_default_workspace;
  }

  private get isDeletingConsolidation() {
    return this.deleteConsolidationMutationObs.getCurrentResult().status === 'pending';
  }

  private get isDeletingOrganisation() {
    return this.deleteOrganisationMutationObs.getCurrentResult().status === 'pending';
  }

  private get isReconnecting() {
    return this.reconnectOrganisationMutationObs.getCurrentResult().status === 'pending';
  }

  private get isRemovingAccess() {
    return this.removeOrganisationAccessMutationObs.getCurrentResult().status === 'pending';
  }

  private get isUpdating() {
    return this.updateMutation.getCurrentResult().status === 'pending';
  }

  protected get consolidations() {
    return this.getResult(this.consolidationsObs).data;
  }

  protected get currentUser() {
    return this.getResult(this.currentUserObs).data;
  }

  protected get integrations() {
    return this.getResult(this.integrationsObs).data;
  }

  protected get rawWorkspaces() {
    return this.getResult(this.workspacesObs).data;
  }

  protected get rawWorkspace() {
    return this.rawWorkspaces?.find(w => w.id === this.workspaceId);
  }

  protected get workspace() {
    const rawWorkspace = this.rawWorkspace;
    const consolidations = this.consolidations;
    const integrations = this.integrations;
    const currentUser = this.currentUser;

    if (!rawWorkspace || !consolidations || !integrations || !currentUser) {
      return undefined;
    }

    return this.memoizedProcessWorkspace(rawWorkspace, integrations, currentUser, consolidations, this.flags);
  }

  protected getNewState(): ModifyWorkspaceViewState {
    return {
      ...super.getNewState(),
      errorReconnecting: this.errorReconnecting,
      errorUpdating: this.errorUpdating,
      isDefaultWorkspace: this.isDefaultWorkspace,
      isDeletingConsolidation: this.isDeletingConsolidation,
      isDeletingOrganisation: this.isDeletingOrganisation,
      isReconnecting: this.isReconnecting,
      isRemovingAccess: this.isRemovingAccess,
      isUpdating: this.isUpdating,
      workspace: this.workspace
    };
  }
}
